import React, { Fragment, useState } from "react";
import { useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import {
  Box,
  Button,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import { fillParent, scrollingBox, footerFlex } from "utils/base-styles";
import { useAuth } from "hooks/use-auth.hook";

import {
  ChangePassword,
  EditUsername,
  ProfileQuickBox,
  ReferralTermsModal,
  useUserData,
} from "features/User";
import {
  Edit,
  Email,
  GroupOutlined,
  LogoutOutlined,
  TuneOutlined,
  ContentCopyOutlined
} from "@mui/icons-material";
import Footer from "components/layout/Footer";
import PageTitle from "components/layout/PageTitle";
import { FeatureHelpVideo } from "features/Support";
import { NavLink } from "react-router-dom";
import { Masonry } from "@mui/lab";
import { getFormattedDate } from "utils/time.util";
import GenericDialog from "components/base/GenericDialog";
import { LoginType } from "features/User";
import { Helmet } from "react-helmet-async";
import { codeFontFamily } from "utils/code-font";

export default function ProfileIndexPage() {
  const {
    groupId,
    groupRole,
    groupName,
    groupAdmins,
    userLoading,
    registrationDate,
    userReferralCode,
    groupReferralCode,
    isReferralHidden,
  } = useUserData();

  const theme = useTheme();
  const { microsoftUser, user } = useSelector((state) => state.auth);
  const { instance } = useMsal();

  const [editUsername, setEditUsername] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [viewAdmins, setViewAdmins] = useState(false);
  const [copyTooltip, setCopyTooltip] = useState(false);
  const [viewReferralTerms, setViewReferralTerms] = useState(false);

  const { handleLogout } = useAuth({
    handleLoading: () => { },
  });

  const isGroupUser = groupId != null;
  const referralCode = groupReferralCode ? groupReferralCode : userReferralCode;
  const canSeeReferralBlock =
    (!isGroupUser || groupRole === "Teacher" || groupRole === "OrganizationAdmin") &&
    !!referralCode &&
    !isReferralHidden;
  const signupUrl = `${window.location.origin}/signup?referral_code=${referralCode}`;

  const handleCopyReferral = () => {
    navigator.clipboard.writeText(signupUrl).then(() => {
      setCopyTooltip(true);
    }).catch((error) => {
      console.error("Unable to copy data to clipboard:", error);
    });
  };

  const handleMouseOut = () => {
    setTimeout(() => {
      setCopyTooltip(false);
    }, 1000);
  };

  const buttonStyles = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
  };

  return (
    <Box sx={{ ...fillParent, ...scrollingBox, ...footerFlex }}>
      <Helmet>
        <title>Sooth - Profile{user?.userName ? ` - @${user?.userName}` : ''}</title>
      </Helmet>
      <Stack direction="column" gap={2}>
        <PageTitle sx={{ wordBreak: "break-word" }}>
          Hey, {user?.userName}.
        </PageTitle>
        <Masonry columns={{ xs: 1, sm: 2, md: 3 }}>
          {!userLoading && (
            <ProfileQuickBox title="Your Info">
              <Stack gap={0.5} sx={{ flex: 1 }} maxWidth="100%">
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <Typography component="div" variant="caption" fontWeight="bold">
                    Email:
                  </Typography>
                  <Tooltip title={user.email} followCursor>
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      {user.email}
                    </Typography>
                  </Tooltip>
                </Stack>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <Typography component="div" variant="caption" fontWeight="bold">
                    Created:
                  </Typography>
                  <Typography variant="body2">
                    {getFormattedDate(registrationDate, "year", 1, true, false)}
                  </Typography>
                </Stack>
              </Stack>
            </ProfileQuickBox>
          )}
          <ProfileQuickBox
            title="Want to change your username?"
            description="Don't like your current username? Click the button below to change it."
          >
            <Button
              onClick={() => setEditUsername(true)}
              variant="outlined"
              startIcon={<Edit />}
              sx={buttonStyles}
            >
              Change Username
            </Button>
          </ProfileQuickBox>
          {user.loginType === LoginType.Simple && (
            <ProfileQuickBox
              title="Need a new password?"
              description="Always use strong passwords to keep your account secure."
            >
              <Button
                onClick={() => setChangePassword(true)}
                variant="outlined"
                startIcon={<Edit />}
                sx={buttonStyles}
              >
                Change Password
              </Button>
            </ProfileQuickBox>
          )}
          <ProfileQuickBox
            title="Want to change your avatar?"
            description="Click on the button below to change your avatar."
          >
            <Button
              variant="outlined"
              startIcon={<Edit />}
              sx={buttonStyles}
              LinkComponent={NavLink}
              to="/profile/preferences?id=change-avatar"
            >
              Change Avatar
            </Button>
          </ProfileQuickBox>
          {canSeeReferralBlock && (
            <ProfileQuickBox
              title={
                <Stack direction="column" alignItems="flex-end" sx={{ width: '100%' }}>
                  <FeatureHelpVideo
                    showText
                    videoId="1168981769"
                    title="Tip: How do I use this"
                    sx={{ mt: -3 }}
                  />
                  <Typography variant="h4" color="primary.main" sx={{ alignSelf: 'flex-start' }}>
                    Refer a friend!
                  </Typography>
                </Stack>
              }
              description={
                <Fragment>
                  Use your referral code to give friends a 10% discount and earn
                  credit towards your next annual subscription!{" "}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => setViewReferralTerms(true)}
                    sx={{ verticalAlign: "baseline" }}
                  >
                    Terms and conditions apply.
                  </Link>
                </Fragment>
              }
            >
              <Tooltip title={copyTooltip ? "Copied to clipboard!" : "Copy referral link"}>
                <Button
                  onClick={handleCopyReferral}
                  variant="outlined"
                  endIcon={<ContentCopyOutlined />}
                  sx={{
                    ...buttonStyles,
                    fontFamily: codeFontFamily,
                    letterSpacing: "0.04em",
                  }}
                  onMouseOut={handleMouseOut}
                >
                  {referralCode}
                </Button>
              </Tooltip>
            </ProfileQuickBox>
          )}
          <ProfileQuickBox
            title="Public Computer?"
            description="Keep your account safe by logging out when you leave a public computer."
          >
            <Button
              onClick={() => {
                handleLogout(microsoftUser, instance);
              }}
              variant="outlined"
              sx={buttonStyles}
              startIcon={<LogoutOutlined />}
            >
              Logout
            </Button>
          </ProfileQuickBox>
        </Masonry>
        {groupId != null && (
          <Fragment>
            <Typography variant="h4" component="h2">
              Group Information
            </Typography>
            <Masonry columns={{ xs: 1, sm: 2, md: 3 }}>
              <ProfileQuickBox
                title="Group Membership"
                description="You are currently a member of a group."
              >
                <Stack gap={0.5} sx={{ flex: 1 }} maxWidth="100%">
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="caption" component="div" fontWeight="bold">
                      Group:
                    </Typography>
                    <Tooltip title={groupName} followCursor>
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {groupName}
                      </Typography>
                    </Tooltip>
                  </Stack>
                </Stack>
              </ProfileQuickBox>
              <ProfileQuickBox
                title="Need help with your Group?"
                description={
                  groupAdmins.length > 1
                    ? "View your Group's administrators to email one for help."
                    : "Click to email your Group's admin."
                }
              >
                {groupAdmins.length > 1 ? (
                  <Button
                    onClick={() => setViewAdmins(true)}
                    variant="outlined"
                    startIcon={<Email />}
                    sx={buttonStyles}
                  >
                    View Admins
                  </Button>
                ) : (
                  <Button
                    LinkComponent={Link}
                    href={`mailto:${groupAdmins[0]}?subject=Help with Sooth Group ${groupName}`}
                    variant="outlined"
                    startIcon={<Email />}
                    sx={buttonStyles}
                  >
                    Email Admin
                  </Button>
                )}
                <GenericDialog
                  isOpen={viewAdmins}
                  handleClose={() => setViewAdmins(false)}
                  title="Click to eamil a Group admin"
                  actions={
                    <Button
                      variant="outlined"
                      onClick={() => setViewAdmins(false)}
                    >
                      Close
                    </Button>
                  }
                >
                  <Stack>
                    {groupAdmins.map((adminEmail) => (
                      <Tooltip
                        title={adminEmail}
                        followCursor
                        key={`${groupId}-${adminEmail}`}
                      >
                        <Link
                          href={`mailto:${adminEmail}?subject=Help with Sooth Group ${groupName}`}
                          variant="body2"
                          noWrap
                        >
                          {adminEmail}
                        </Link>
                      </Tooltip>
                    ))}
                  </Stack>
                </GenericDialog>
              </ProfileQuickBox>
              {groupRole === "OrganizationAdmin" && (
                <Fragment>
                  <ProfileQuickBox
                    title="Manage your group's users?"
                    description="View and manage user information from the user management screen."
                  >
                    <Button
                      LinkComponent={NavLink}
                      to="/profile/group/user-management"
                      variant="outlined"
                      startIcon={<GroupOutlined />}
                      sx={buttonStyles}
                    >
                      User Management
                    </Button>
                  </ProfileQuickBox>
                  <ProfileQuickBox
                    title="Add Group Users?"
                    description="Add users individually and in bulk, manage group codes, and auto-join domains."
                  >
                    <Button
                      LinkComponent={NavLink}
                      to="/profile/group/add-users"
                      variant="outlined"
                      startIcon={<TuneOutlined />}
                      sx={buttonStyles}
                    >
                      Add Users
                    </Button>
                  </ProfileQuickBox>
                </Fragment>
              )}
            </Masonry>
          </Fragment>
        )}
        {/* <Stats /> */}
      </Stack>
      <Footer sx={{ px: 0 }} />
      {editUsername && (
        <EditUsername
          editUsername={editUsername}
          setEditUsername={editUsername}
          onClose={() => setEditUsername(false)}
        />
      )}
      {changePassword && (
        <ChangePassword
          changePassword={changePassword}
          setChangePassword={setChangePassword}
          onClose={() => setChangePassword(false)}
        />
      )}
      {viewReferralTerms && (
        <ReferralTermsModal
          isOpen={viewReferralTerms}
          onClose={() => setViewReferralTerms(false)}
        />
      )}
    </Box>
  );
}
